import React, { useEffect, useRef, useMemo, memo, useCallback, useState } from 'react';
import { ScrollView, TouchableOpacity, Animated, View } from 'react-native';
import { Chip, Grid, IconButton, Typography, useTheme, Box, Checkbox, Badge, Drawer, Divider, Slider, Collapse } from '@mui/material';
import { Categories } from '../../../../app/stores/Product';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { navigationStore } from '../../../../app/stores/Navigation';
import { appConfigStore } from '../../../../app/stores/AppConfig';
import trackingService, { ActivityType } from '../../../../services/analytics/TrackingService';

interface SubcategoryOption {
    subcategory: string;
    totalProducts: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE — horizontal chip scroller + filter drawers
// ─────────────────────────────────────────────────────────────────────────────

interface CategoriesFilterScrollerProps {
    scrollViewRef: React.RefObject<ScrollView>;
    allCategories: Categories[];
    selectedCategories: Categories[];
    setSelectedCategories: (categories: Categories[]) => void;
    getProductsLength: (category: string) => number;
    availableSubcategories: SubcategoryOption[];
    selectedSubcategory: string | null;
    setSelectedSubcategory: (subcategory: string | null) => void;
    sortOrder: 'price_asc' | 'price_desc' | null;
    setSortOrder: (order: 'price_asc' | 'price_desc' | null) => void;
}

export const CategoriesFilterScroller = memo(({
    allCategories,
    getProductsLength,
    scrollViewRef,
    selectedCategories,
    setSelectedCategories,
    availableSubcategories,
    selectedSubcategory,
    setSelectedSubcategory,
    sortOrder,
    setSortOrder,
}: CategoriesFilterScrollerProps) => {
    const theme = useTheme();
    const darkMode = appConfigStore((state) => state.darkMode);
    const { config } = useStoreConfig();
    const scrollViewScrollerRef = useRef<ScrollView>(scrollViewRef as unknown as ScrollView);
    const scrollPosScrollerRef = useRef(0);
    const [sortDrawerOpen, setSortDrawerOpen] = useState(false);
    const [subcatDrawerOpen, setSubcatDrawerOpen] = useState(false);
    const pulseAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        const pulse = () => {
            Animated.sequence([
                Animated.timing(pulseAnim, { toValue: 1.2, duration: 400, useNativeDriver: true }),
                Animated.timing(pulseAnim, { toValue: 1, duration: 380, useNativeDriver: true }),
            ]).start();
        };
        pulse();
        const interval = setInterval(pulse, 5000);
        return () => clearInterval(interval);
    }, []);

    const getIsSelected = (cat: string) => selectedCategories.includes(cat as unknown as Categories);

    useEffect(() => {
        if (scrollViewScrollerRef.current) {
            (scrollViewScrollerRef.current).scrollTo({ x: scrollPosScrollerRef.current, animated: false });
        }
    }, [scrollViewScrollerRef.current]);

    const hasSubcategories = availableSubcategories.length > 0;
    const hasSortActive = sortOrder !== null;
    const hasSubcatActive = selectedSubcategory !== null;

    const categoryChips = useMemo(() => {
        const sortedCategories = [...allCategories].sort((a, b) => {
            if (a.category === 'All') return -1;
            if (b.category === 'All') return 1;
            return a.category.localeCompare(b.category);
        });

        return sortedCategories.map((cat, index) => {
            const isSelectedCategory = getIsSelected(cat.category);
            const label = isSelectedCategory
                ? `${cat.category} (${getProductsLength(cat.category)} Items)`
                : cat.category;

            return (
                <Chip
                    component='div'
                    key={`${cat}-${index}`}
                    label={label}
                    variant={'filled'}
                    size='medium'
                    sx={{ px: 0.5 }}
                    style={{
                        letterSpacing: 0.5,
                        fontWeight: '600',
                        minHeight: 40,
                        marginRight: 8,
                        borderRadius: 20,
                        backgroundColor: isSelectedCategory ? config.primaryColor : darkMode ? '#333333' : '#ececec',
                        color: isSelectedCategory ? 'white' : theme.palette.text.primary,
                    }}
                    onClick={() => {
                        setSelectedCategories([cat.category as unknown as Categories]);
                        trackingService.trackActivity({
                            activityType: ActivityType.CATEGORY_FILTERING,
                            screenName: 'Shop',
                            activityData: { category: cat.category, productCount: cat.totalProducts },
                        }).catch(error => console.error('Failed to track category filter:', error));
                    }}
                />
            );
        });
    }, [allCategories, selectedCategories, getProductsLength, darkMode, theme.palette.text.primary]);

    const drawerPaperSx = {
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        backgroundColor: theme.customColors.background,
        maxHeight: '80vh',
    };

    const DrawerHandle = () => (
        <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: theme.palette.divider, mx: 'auto', mt: 1.5, mb: 0.5 }} />
    );

    const sortOptions: { value: 'price_asc' | 'price_desc' | null; label: string; icon: React.ReactNode }[] = [
        { value: null, label: 'Default', icon: <SortRoundedIcon fontSize='small' /> },
        { value: 'price_asc', label: 'Price: Low → High', icon: <ArrowUpwardRoundedIcon fontSize='small' /> },
        { value: 'price_desc', label: 'Price: High → Low', icon: <ArrowDownwardRoundedIcon fontSize='small' /> },
    ];

    return (
        <Grid
            item
            xs={12}
            display={{ md: 'none' }}
            sx={{
                backgroundColor: theme.customColors.background,
                boxShadow: '0px 10px 10px rgba(0, 0, 0, 0.1)',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                marginTop: -.5,
                overflow: 'hidden',
            }}
        >
            {/* Category chips row + right-side action buttons */}
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <ScrollView
                    horizontal
                    style={{ flex: 1 }}
                    contentContainerStyle={{ paddingTop: 22, paddingBottom: 22, paddingLeft: 15 }}
                    ref={scrollViewScrollerRef}
                    onScroll={(event) => { scrollPosScrollerRef.current = event.nativeEvent.contentOffset.x; }}
                    scrollEventThrottle={16}
                    showsHorizontalScrollIndicator={false}
                >
                    {categoryChips}
                </ScrollView>

                {/* Right-side filter buttons */}
                <View
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        paddingHorizontal: 8,
                        paddingTop: 22,
                        paddingBottom: 22,
                        borderLeftWidth: 1,
                        borderLeftColor: theme.palette.divider,
                        gap: 2,
                    }}
                >
                    {/* Subcategory button — only when subcategories exist */}
                    {hasSubcategories && (
                        <Animated.View style={{ transform: [{ scale: pulseAnim }], overflow: 'visible' }}>
                            <Badge
                                variant='dot'
                                invisible={!hasSubcatActive}
                                sx={{ '& .MuiBadge-dot': { backgroundColor: config.primaryColor, width: 10, height: 10, borderRadius: '50%' } }}
                            >
                                <IconButton
                                    onClick={() => setSubcatDrawerOpen(true)}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        color: hasSubcatActive ? config.primaryColor : theme.palette.text.secondary,
                                        backgroundColor: hasSubcatActive ? `${config.primaryColor}18` : 'transparent',
                                        '&:hover': { backgroundColor: `${config.primaryColor}22` },
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    <LabelRoundedIcon />
                                </IconButton>
                            </Badge>
                        </Animated.View>
                    )}

                    {/* Price sort button — always visible */}
                    <Animated.View style={{ transform: [{ scale: pulseAnim }], overflow: 'visible' }}>
                        <Badge
                            variant='dot'
                            invisible={!hasSortActive}
                            sx={{ '& .MuiBadge-dot': { backgroundColor: config.primaryColor, width: 10, height: 10, borderRadius: '50%' } }}
                        >
                            <IconButton
                                onClick={() => setSortDrawerOpen(true)}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    color: hasSortActive ? config.primaryColor : theme.palette.text.secondary,
                                    backgroundColor: hasSortActive ? `${config.primaryColor}18` : 'transparent',
                                    '&:hover': { backgroundColor: `${config.primaryColor}22` },
                                    transition: 'all 0.2s',
                                }}
                            >
                                <TuneRoundedIcon />
                            </IconButton>
                        </Badge>
                    </Animated.View>
                </View>
            </View>

            {/* ── Price Sort Drawer ─────────────────────────────────────── */}
            <Drawer
                anchor='bottom'
                open={sortDrawerOpen}
                onClose={() => setSortDrawerOpen(false)}
                PaperProps={{ sx: drawerPaperSx }}
            >
                <DrawerHandle />
                <Box sx={{ px: 2.5, pt: 1.5, pb: 4 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TuneRoundedIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                            <Typography variant='subtitle1' fontWeight='700'>Sort by Price</Typography>
                        </Box>
                        <IconButton size='small' onClick={() => setSortDrawerOpen(false)}>
                            <CloseRoundedIcon fontSize='small' />
                        </IconButton>
                    </Box>

                    {/* Sort options */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {sortOptions.map((opt) => {
                            const isActive = sortOrder === opt.value;
                            return (
                                <TouchableOpacity
                                    key={String(opt.value)}
                                    onPress={() => {
                                        setSortOrder(opt.value);
                                        setSortDrawerOpen(false);
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 2,
                                            py: 1.5,
                                            borderRadius: 2,
                                            border: `1.5px solid ${isActive ? config.primaryColor : theme.palette.divider}`,
                                            backgroundColor: isActive ? `${config.primaryColor}14` : 'transparent',
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <Box sx={{ color: isActive ? config.primaryColor : 'text.secondary', display: 'flex' }}>
                                            {opt.icon}
                                        </Box>
                                        <Typography
                                            variant='body2'
                                            sx={{ fontWeight: isActive ? 700 : 400, color: isActive ? config.primaryColor : 'text.primary', flex: 1 }}
                                        >
                                            {opt.label}
                                        </Typography>
                                        {isActive && (
                                            <Box
                                                sx={{
                                                    width: 8, height: 8, borderRadius: '50%',
                                                    backgroundColor: config.primaryColor,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </TouchableOpacity>
                            );
                        })}
                    </Box>
                </Box>
            </Drawer>

            {/* ── Subcategory Drawer ────────────────────────────────────── */}
            {hasSubcategories && (
                <Drawer
                    anchor='bottom'
                    open={subcatDrawerOpen}
                    onClose={() => setSubcatDrawerOpen(false)}
                    PaperProps={{ sx: drawerPaperSx }}
                >
                    <DrawerHandle />
                    <Box sx={{ px: 2.5, pt: 1.5, pb: 4 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LabelRoundedIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                                <Typography variant='subtitle1' fontWeight='700'>Filter by Brand</Typography>
                            </Box>
                            <IconButton size='small' onClick={() => setSubcatDrawerOpen(false)}>
                                <CloseRoundedIcon fontSize='small' />
                            </IconButton>
                        </Box>
                        {selectedSubcategory && (
                            <TouchableOpacity onPress={() => setSelectedSubcategory(null)}>
                                <Typography variant='caption' sx={{ color: config.primaryColor, fontWeight: 600, mb: 1.5, display: 'block' }}>
                                    Clear selection
                                </Typography>
                            </TouchableOpacity>
                        )}
                        <Divider sx={{ mb: 1 }} />

                        {/* Subcategory list */}
                        <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 360 }}>
                            {availableSubcategories.map((opt) => {
                                const isSelected = selectedSubcategory === opt.subcategory;
                                return (
                                    <TouchableOpacity
                                        key={opt.subcategory}
                                        onPress={() => {
                                            setSelectedSubcategory(isSelected ? null : opt.subcategory);
                                            setSubcatDrawerOpen(false);
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                py: 0.75,
                                                borderRadius: 1,
                                                backgroundColor: isSelected ? `${config.primaryColor}10` : 'transparent',
                                                transition: 'background-color 0.15s',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                size='small'
                                                disableRipple
                                                sx={{ p: 0.75, '&.Mui-checked': { color: config.primaryColor } }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant='body2'
                                                    sx={{ fontWeight: isSelected ? 700 : 400, color: isSelected ? config.primaryColor : 'text.primary' }}
                                                >
                                                    {opt.subcategory}
                                                </Typography>
                                                <Typography variant='caption' sx={{ color: 'text.disabled' }}>
                                                    {opt.totalProducts} Items
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TouchableOpacity>
                                );
                            })}
                        </ScrollView>
                    </Box>
                </Drawer>
            )}
        </Grid>
    );
});


// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP — sticky side menu with always-visible nested subcategories
// ─────────────────────────────────────────────────────────────────────────────

export interface CategoriesFilterSideMenuProps {
    scrollViewRef: React.RefObject<ScrollView>;
    allCategories: Categories[];
    selectedCategories: Categories[];
    /** Raw category setter — sidebar manages subcategory reset internally */
    setSelectedCategories: (categories: Categories[]) => void;
    getProductsLength: (category: string) => number;
    /** Subcategories grouped by category, pre-computed for ALL categories */
    allSubcategoriesByCategory: Record<string, SubcategoryOption[]>;
    selectedSubcategory: string | null;
    setSelectedSubcategory: (subcategory: string | null) => void;
    priceRange: [number, number];
    setPriceRange: (range: [number, number]) => void;
    maxPrice: number;
}

export const CategoriesFilterSideMenu = memo(({
    scrollViewRef,
    allCategories,
    selectedCategories,
    setSelectedCategories,
    getProductsLength,
    allSubcategoriesByCategory,
    selectedSubcategory,
    setSelectedSubcategory,
    priceRange,
    setPriceRange,
    maxPrice,
}: CategoriesFilterSideMenuProps) => {
    const theme = useTheme();
    const darkMode = appConfigStore((state) => state.darkMode);
    const { config } = useStoreConfig();
    const currency = config.currency ?? 'R';
    const { sideMenuIsExpanded, updateSideMenuIsExpanded } = navigationStore(state => state);
    const scrollViewSideRef = useRef<ScrollView>(scrollViewRef as unknown as ScrollView);
    const scrollPosSideRef = useRef(0);
    const animatedWidth = useRef(new Animated.Value(sideMenuIsExpanded ? 1 : 0)).current;
    const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
    const [priceExpanded, setPriceExpanded] = useState(true);

    const toggleMenu = useCallback(() => {
        updateSideMenuIsExpanded(!sideMenuIsExpanded);
        Animated.timing(animatedWidth, {
            toValue: !sideMenuIsExpanded ? 1 : 0,
            duration: 300,
            useNativeDriver: false,
        }).start();
    }, [sideMenuIsExpanded, updateSideMenuIsExpanded]);

    const getIsSelected = (cat: string) => selectedCategories.includes(cat as unknown as Categories);

    useEffect(() => {
        if (scrollViewSideRef.current) {
            const scrollView = scrollViewSideRef.current as unknown as ScrollView;
            scrollView.scrollTo({ y: scrollPosSideRef.current, animated: false });
        }
    }, [scrollViewSideRef.current]);

    const categoryItems = useMemo(() => {
        const allCat = allCategories.find(c => c.category === 'All');
        const sortedCategories = [...allCategories]
            .filter(c => c.category !== 'All')
            .sort((a, b) => a.category.localeCompare(b.category));

        const isAllSelected = getIsSelected('All');

        const baseItemSx = (isSelected: boolean) => ({
            px: '14px',
            py: 0.75,
            borderRadius: 2,
            minWidth: 160,
            backgroundColor: isSelected ? config.primaryColor : 'transparent',
            transition: 'background-color 0.25s, opacity 0.25s',
            ':hover': { backgroundColor: isSelected ? config.primaryColor : darkMode ? '#2a2a2a' : '#f0f0f0' },
            opacity: sideMenuIsExpanded ? 1 : 0,
            cursor: 'pointer',
        });

        const items: React.ReactNode[] = [];

        // ── All ───────────────────────────────────────────────────────────────
        if (allCat) {
            items.push(
                <TouchableOpacity
                    key='All'
                    onPress={() => {
                        setSelectedCategories(['All' as unknown as Categories]);
                        setSelectedSubcategory(null);
                        trackingService.trackActivity({
                            activityType: ActivityType.CATEGORY_FILTERING,
                            screenName: 'Shop',
                            activityData: { category: 'All', productCount: allCat.totalProducts },
                        }).catch(error => console.error('Failed to track category filter:', error));
                    }}
                    style={{ marginRight: 18, marginBottom: 4 }}
                >
                    <Box sx={baseItemSx(isAllSelected)}>
                        <Typography
                            variant='body2'
                            noWrap
                            sx={{
                                letterSpacing: 0.5,
                                fontWeight: '700',
                                color: isAllSelected ? 'white' : theme.palette.text.primary,
                            }}
                        >
                            {isAllSelected
                                ? `All (${getProductsLength('All')} Items)`
                                : 'All'}
                        </Typography>
                    </Box>
                </TouchableOpacity>
            );
        }

        // ── Categories + nested subcategories ─────────────────────────────────
        sortedCategories.forEach((cat) => {
            const isCatSelected = getIsSelected(cat.category);
            const isCatActive = isCatSelected && !selectedSubcategory;
            const subcats = allSubcategoriesByCategory[cat.category] || [];
            const hasSubcats = subcats.length > 0;
            const isHovered = hoveredCategory === cat.category;
            // Reveal subcats on hover OR when this category is selected
            const showSubcats = hasSubcats && sideMenuIsExpanded && (isCatSelected || isHovered);

            items.push(
                <View
                    key={cat.category}
                    // @ts-ignore — onMouseEnter/onMouseLeave are valid on React Native Web
                    onMouseEnter={() => setHoveredCategory(cat.category)}
                    onMouseLeave={() => setHoveredCategory(null)}
                    style={{ marginBottom: showSubcats ? 2 : 0 }}
                >
                    {/* Category row */}
                    <TouchableOpacity
                        onPress={() => {
                            setSelectedCategories([cat.category as unknown as Categories]);
                            setSelectedSubcategory(null);
                            trackingService.trackActivity({
                                activityType: ActivityType.CATEGORY_FILTERING,
                                screenName: 'Shop',
                                activityData: { category: cat.category, productCount: cat.totalProducts },
                            }).catch(error => console.error('Failed to track category filter:', error));
                        }}
                        style={{ marginRight: 18, marginBottom: showSubcats ? 1 : 8 }}
                    >
                        <Box sx={baseItemSx(isCatActive)}>
                            <Typography
                                variant='body2'
                                noWrap
                                sx={{
                                    letterSpacing: 0.5,
                                    fontWeight: '600',
                                    color: isCatActive ? 'white' : theme.palette.text.primary,
                                }}
                            >
                                {isCatActive
                                    ? `${cat.category} (${getProductsLength(cat.category)} Items)`
                                    : cat.category}
                            </Typography>
                        </Box>
                    </TouchableOpacity>

                    {/* Subcategory rows — revealed on hover or when this category is selected */}
                    {showSubcats && (
                        <View style={{ marginRight: 18, marginBottom: 8 }}>
                            {subcats.map((opt) => {
                                const isSubSelected = isCatSelected && selectedSubcategory === opt.subcategory;
                                return (
                                    <TouchableOpacity
                                        key={opt.subcategory}
                                        onPress={() => {
                                            setSelectedCategories([cat.category as unknown as Categories]);
                                            setSelectedSubcategory(isSubSelected ? null : opt.subcategory);
                                        }}
                                        style={{ marginBottom: 1 }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                pl: '10px',
                                                pr: '14px',
                                                py: 0.25,
                                                borderRadius: 2,
                                                minWidth: 160,
                                                backgroundColor: isSubSelected
                                                    ? `${config.primaryColor}14`
                                                    : 'transparent',
                                                transition: 'background-color 0.2s',
                                                ':hover': {
                                                    backgroundColor: isSubSelected
                                                        ? `${config.primaryColor}22`
                                                        : darkMode ? '#242424' : '#f5f5f5',
                                                },
                                                opacity: sideMenuIsExpanded ? 1 : 0,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isSubSelected}
                                                size='small'
                                                disableRipple
                                                sx={{
                                                    p: 0.5,
                                                    mr: 0.5,
                                                    color: darkMode ? '#555' : '#bbb',
                                                    '&.Mui-checked': { color: config.primaryColor },
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box>
                                                <Typography
                                                    variant='caption'
                                                    noWrap
                                                    sx={{
                                                        letterSpacing: 0.3,
                                                        fontWeight: isSubSelected ? '600' : '400',
                                                        color: isSubSelected
                                                            ? config.primaryColor
                                                            : theme.palette.text.primary,
                                                        display: 'block',
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {opt.subcategory}
                                                </Typography>
                                                <Typography
                                                    variant='caption'
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        color: theme.palette.text.disabled,
                                                        display: 'block',
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {opt.totalProducts} Items
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                    )}
                </View>
            );
        });

        return items;
    }, [
        allCategories,
        selectedCategories,
        selectedSubcategory,
        getProductsLength,
        sideMenuIsExpanded,
        darkMode,
        allSubcategoriesByCategory,
        hoveredCategory,
        theme.palette.text.primary,
        theme.palette.text.secondary,
        config.primaryColor,
    ]);

    const menuWidth = animatedWidth.interpolate({
        inputRange: [0, 1],
        outputRange: ['13%', '25%'],
    });

    return (
        <>
            <Animated.View
                // @ts-ignore
                style={{
                    width: menuWidth,
                    display: 'flex',
                    flexShrink: 0,
                    position: 'sticky', // never change this from sticky to anything else
                    top: 16,
                    alignSelf: 'flex-start',
                    maxHeight: 'calc(100vh - 150px)',
                    overflow: 'visible',
                    zIndex: 100,
                }}
            >
                <Grid
                    item
                    display={{ xs: 'none', md: 'flex' }}
                    sx={{
                        overflowY: 'auto',
                        position: 'relative',
                        overflowX: 'auto',
                    }}
                >
                    {/* Toggle button */}
                    <div
                        style={{
                            display: 'flex',
                            position: 'absolute',
                            right: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            width: 40,
                            height: 40,
                            borderRadius: 20,
                            backgroundColor: config.primaryColor,
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'visible',
                        }}
                    >
                        <IconButton onClick={toggleMenu} size='small' sx={{ color: 'white' }}>
                            {sideMenuIsExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </div>

                    <ScrollView
                        contentContainerStyle={{
                            alignSelf: 'flex-end',
                            marginTop: 18,
                            marginRight: 22,
                            borderRightWidth: 2,
                            borderColor: theme.palette.divider,
                            borderRadius: 10,
                            paddingVertical: 30,
                            backgroundColor: theme.customColors.background,
                        }}
                        ref={scrollViewSideRef}
                        onScroll={(event) => {
                            scrollPosSideRef.current = event.nativeEvent.contentOffset.y;
                        }}
                        scrollEventThrottle={16}
                        showsVerticalScrollIndicator={false}
                    >
                        {categoryItems}

                        {/* ── Price filter ─────────────────────────────────── */}
                        {maxPrice > 0 && sideMenuIsExpanded && (
                            <Box sx={{ px: '14px', pt: 1, pb: 0.5, minWidth: 160, opacity: sideMenuIsExpanded ? 1 : 0 }}>
                                <Divider sx={{ mb: 1.5 }} />

                                {/* Header row */}
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 0.5 }}
                                    onClick={() => setPriceExpanded(p => !p)}
                                >
                                    <Typography variant='body2' sx={{ fontWeight: 700, letterSpacing: 0.5, color: theme.palette.text.primary }}>
                                        Price
                                    </Typography>
                                    <ExpandMoreRoundedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: theme.palette.text.secondary,
                                            transition: 'transform 0.2s',
                                            transform: priceExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}
                                    />
                                </Box>

                                <Collapse in={priceExpanded}>
                                    {/* Info + Reset */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                                            Max {currency} {maxPrice.toLocaleString()}
                                        </Typography>
                                        {(priceRange[0] > 0 || priceRange[1] < maxPrice) && (
                                            <Typography
                                                variant='caption'
                                                sx={{ color: config.primaryColor, cursor: 'pointer', fontWeight: 600 }}
                                                onClick={() => setPriceRange([0, maxPrice])}
                                            >
                                                Reset
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Range labels */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.5 }}>
                                        <Typography variant='caption' sx={{ color: theme.palette.text.disabled }}>
                                            {currency} {priceRange[0].toLocaleString()}
                                        </Typography>
                                        <Typography variant='caption' sx={{ color: theme.palette.text.disabled }}>
                                            {currency} {priceRange[1].toLocaleString()}
                                        </Typography>
                                    </Box>

                                    {/* Dual-handle slider */}
                                    <Slider
                                        value={priceRange}
                                        onChange={(_, val) => setPriceRange(val as [number, number])}
                                        min={0}
                                        max={maxPrice}
                                        disableSwap
                                        size='small'
                                        sx={{
                                            color: config.primaryColor,
                                            '& .MuiSlider-thumb': {
                                                width: 14,
                                                height: 14,
                                                '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 6px ${config.primaryColor}26` },
                                            },
                                            '& .MuiSlider-rail': { opacity: 0.3 },
                                        }}
                                    />
                                </Collapse>
                            </Box>
                        )}
                    </ScrollView>
                </Grid>
            </Animated.View>
        </>
    );
});
